import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { ActiveGlow } from "@/components/launch/ActiveGlow";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { EventSourceMessageJournal } from "@/components/dashboard/createEventSource";
import type { JournalType } from "@/types";
import { Check, Copy, Maximize2, Minimize2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

type TerminalStatus = "running" | "completed" | "error" | "cancelling";

// Color each log line by its journal type. Types without an entry fall back to muted output.
const LINE_COLOR: Partial<Record<JournalType, string>> = {
  error: "text-destructive",
  warn: "text-warning",
  health: "text-success"
};

const toMs = (value: Date | string | number): number =>
  value instanceof Date
    ? value.getTime()
    : typeof value === "number"
      ? value
      : new Date(value).getTime();

// The status badge owns all status. Soft tinted chips (not solid fills) — "Live" mirrors "Failed".
const STATUS_META: Record<TerminalStatus, { label: string; pill: string; dot: string }> = {
  running: { label: "Running", pill: "bg-secondary text-secondary-foreground", dot: "" },
  cancelling: { label: "Cancelling", pill: "bg-secondary text-secondary-foreground", dot: "" },
  completed: { label: "Live", pill: "bg-success/15 text-success", dot: "bg-success" },
  error: { label: "Failed", pill: "bg-destructive/15 text-destructive", dot: "bg-destructive" }
};

export const ProvisionTerminal = ({
  logs,
  status,
  startedAt,
  endedAt,
  phaseLabel
}: {
  logs: EventSourceMessageJournal[];
  status: TerminalStatus;
  /** Current phase name shown as the running badge label (e.g. "Deploying app"). */
  phaseLabel?: string;
  /** Authoritative task start time; keeps the timer stable across reloads. */
  startedAt?: Date | string | number | null;
  /** Authoritative task end time, used to freeze the timer once finished. */
  endedAt?: Date | string | number | null;
}) => {
  // The journal arrives newest-first; sort ascending by time so output reads top to bottom.
  const lines = useMemo(
    () => [...logs].sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt)),
    [logs]
  );

  const isRunning = status === "running" || status === "cancelling";
  const meta = STATUS_META[status] ?? STATUS_META.running;

  const scrollRef = useRef<HTMLDivElement>(null);
  const followRef = useRef(true);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    // Keep following the tail only while the user is parked near the bottom.
    followRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 32;
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !followRef.current) {
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [lines]);

  // Anchor the timer to the task's own start time when provided so it survives reloads and
  // never drifts as the log window slides; fall back to the first visible log otherwise.
  const startMs = useMemo(() => {
    if (startedAt != null) {
      return toMs(startedAt);
    }
    return lines.length ? +new Date(lines[0].createdAt) : null;
  }, [startedAt, lines]);

  // Tick once a second while running so the elapsed timer stays live.
  useEffect(() => {
    if (!isRunning) {
      return;
    }
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  const elapsedLabel = useMemo(() => {
    if (startMs === null) {
      return null;
    }
    const end = isRunning
      ? now
      : endedAt != null
        ? toMs(endedAt)
        : lines.length
          ? +new Date(lines[lines.length - 1].createdAt)
          : now;
    const seconds = Math.max(0, Math.round((end - startMs) / 1000));
    const minutes = Math.floor(seconds / 60);
    return minutes > 0 ? `${minutes}m ${seconds % 60}s` : `${seconds}s`;
  }, [startMs, isRunning, now, endedAt, lines]);

  const handleCopy = () => {
    navigator.clipboard.writeText(lines.map(l => l.message).join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleToggleExpand = () => {
    setExpanded(expanded => !expanded);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <ActiveGlow status={status} className="w-full">
        <div className="group w-full overflow-hidden rounded-xl border bg-card/50 shadow-sm">
          <div className="flex items-center justify-between border-b px-4 py-2.5">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium",
                meta.pill
              )}
            >
              {isRunning ? (
                <Spinner className="size-3" />
              ) : (
                <span className={cn("size-1.5 rounded-full", meta.dot)} aria-hidden="true" />
              )}
              {status === "running" ? (phaseLabel ?? meta.label) : meta.label}
            </span>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5 md:opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      animations="none"
                      className="size-7 text-muted-foreground"
                      onClick={handleCopy}
                      disabled={lines.length === 0}
                      aria-label="Copy log output"
                    >
                      {copied ? (
                        <Check className="size-3.5" strokeWidth={1.5} />
                      ) : (
                        <Copy className="size-3.5" strokeWidth={1.5} />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{copied ? "Copied" : "Copy output"}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      animations="none"
                      className="size-7 text-muted-foreground"
                      onClick={handleToggleExpand}
                      aria-label={expanded ? "Collapse output" : "Expand output"}
                    >
                      {expanded ? (
                        <Minimize2 className="size-3.5" strokeWidth={1.5} />
                      ) : (
                        <Maximize2 className="size-3.5" strokeWidth={1.5} />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{expanded ? "Collapse" : "Expand"}</TooltipContent>
                </Tooltip>
              </div>
              {elapsedLabel ? (
                <span className="font-mono text-xs tabular-nums text-muted-foreground">
                  {elapsedLabel}
                </span>
              ) : null}
            </div>
          </div>

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className={cn(
              "themed-scrollbar overflow-y-auto px-4 py-3.5",
              "[mask-image:linear-gradient(to_bottom,transparent,black_14px,black_calc(100%_-_14px),transparent)]",
              "[-webkit-mask-image:linear-gradient(to_bottom,transparent,black_14px,black_calc(100%_-_14px),transparent)]",
              expanded ? "h-[680px]" : "h-[440px]"
            )}
          >
            {lines.length === 0 ? (
              <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                {isRunning ? "Waiting for output…" : "No output."}
              </div>
            ) : (
              <div className="flex flex-col gap-0.5 font-mono text-xs leading-relaxed">
                {lines.map(l => (
                  <div
                    key={l.id}
                    className={cn(
                      "whitespace-pre-wrap break-words",
                      LINE_COLOR[l.type] ?? "text-muted-foreground"
                    )}
                  >
                    {l.message}
                  </div>
                ))}
                {isRunning ? (
                  <span
                    className="inline-block h-3.5 w-2 animate-[caret-blink_1s_steps(1)_infinite] bg-muted-foreground align-middle"
                    aria-hidden="true"
                  />
                ) : null}
              </div>
            )}
          </div>
        </div>
      </ActiveGlow>
    </TooltipProvider>
  );
};
